import * as React from 'react';
import { StripeError } from '@stripe/stripe-js';
import jsSHA from 'jssha';
import { LANG_CODE_ZH_CN } from '../external-constants/extern-constants';
import { checkPaymentAppAvailability, getCordova, info } from '../globals';
import { t } from '../i18n';
import { CurrencyOption, PaymentMethodOption } from '../shopping/ajax-data/ajax-data-types';
import {format} from 'date-fns';

const browserify_crypto = require('browserify-aes');


export function isNumeric(value:string):boolean {
    if(!value) return false;

    const n = parseFloat(value);
    return !isNaN(n) && isFinite(n);
}

export function round(value:any, exp:number) {
    if (typeof exp === 'undefined' || +exp === 0)
      return Math.round(value);
  
    value = +value;
    exp = +exp;
  
    if (isNaN(value) || !(typeof exp === 'number' && exp % 1 === 0))
      return NaN;
  
    // Shift
    value = value.toString().split('e');
    value = Math.round(+(value[0] + 'e' + (value[1] ? (+value[1] + exp) : exp)));
  
    // Shift back
    value = value.toString().split('e');
    return +(value[0] + 'e' + (value[1] ? (+value[1] - exp) : -exp));
}

export function genPaymentKey(password:string):string{
  var shaObj = new jsSHA("SHA3-256", "TEXT");
  shaObj.update(password);
  return shaObj.getHash("HEX");
} 

const iv = '3a3371263134b97f01b29a109d9aec6e1d9f6301fa829d3e25a293f7cb30becb'.substring(0,32);

/**
 * @param message 
 * @param key in hexdecimal format
 */
export function encryptPayment(message:string, key:string):string {
    info("crypto "+browserify_crypto);
    let keyBytes = Buffer.from(key, 'hex');
    info('encryptPayment '+key);
    // encrypt the message with key using AES256-CBC algorithm. what is the default padding?
    // @ts-ignore
    let cipher = browserify_crypto.createCipheriv('aes-256-cbc', keyBytes, Buffer.from(iv, 'hex'));
    return cipher.update(message, 'utf-8', 'hex')+cipher.final('hex');
}

/**
 * see https://stackoverflow.com/questions/11598786/how-to-replace-non-printable-unicode-characters-javascript
 * @param s
 * @returns empty string if s has is null or undefined
 */
export function stripNonPrintableChars(s:string){
  if(!s) return '';

  let re = /[\0-\x1F\x7F-\x9F\xAD\u0378\u0379\u037F-\u0383\u038B\u038D\u03A2\u0528-\u0530\u0557\u0558\u0560\u0588\u058B-\u058E\u0590\u05C8-\u05CF\u05EB-\u05EF\u05F5-\u0605\u061C\u061D\u06DD\u070E\u070F\u074B\u074C\u07B2-\u07BF\u07FB-\u07FF\u082E\u082F\u083F\u085C\u085D\u085F-\u089F\u08A1\u08AD-\u08E3\u08FF\u0978\u0980\u0984\u098D\u098E\u0991\u0992\u09A9\u09B1\u09B3-\u09B5\u09BA\u09BB\u09C5\u09C6\u09C9\u09CA\u09CF-\u09D6\u09D8-\u09DB\u09DE\u09E4\u09E5\u09FC-\u0A00\u0A04\u0A0B-\u0A0E\u0A11\u0A12\u0A29\u0A31\u0A34\u0A37\u0A3A\u0A3B\u0A3D\u0A43-\u0A46\u0A49\u0A4A\u0A4E-\u0A50\u0A52-\u0A58\u0A5D\u0A5F-\u0A65\u0A76-\u0A80\u0A84\u0A8E\u0A92\u0AA9\u0AB1\u0AB4\u0ABA\u0ABB\u0AC6\u0ACA\u0ACE\u0ACF\u0AD1-\u0ADF\u0AE4\u0AE5\u0AF2-\u0B00\u0B04\u0B0D\u0B0E\u0B11\u0B12\u0B29\u0B31\u0B34\u0B3A\u0B3B\u0B45\u0B46\u0B49\u0B4A\u0B4E-\u0B55\u0B58-\u0B5B\u0B5E\u0B64\u0B65\u0B78-\u0B81\u0B84\u0B8B-\u0B8D\u0B91\u0B96-\u0B98\u0B9B\u0B9D\u0BA0-\u0BA2\u0BA5-\u0BA7\u0BAB-\u0BAD\u0BBA-\u0BBD\u0BC3-\u0BC5\u0BC9\u0BCE\u0BCF\u0BD1-\u0BD6\u0BD8-\u0BE5\u0BFB-\u0C00\u0C04\u0C0D\u0C11\u0C29\u0C34\u0C3A-\u0C3C\u0C45\u0C49\u0C4E-\u0C54\u0C57\u0C5A-\u0C5F\u0C64\u0C65\u0C70-\u0C77\u0C80\u0C81\u0C84\u0C8D\u0C91\u0CA9\u0CB4\u0CBA\u0CBB\u0CC5\u0CC9\u0CCE-\u0CD4\u0CD7-\u0CDD\u0CDF\u0CE4\u0CE5\u0CF0\u0CF3-\u0D01\u0D04\u0D0D\u0D11\u0D3B\u0D3C\u0D45\u0D49\u0D4F-\u0D56\u0D58-\u0D5F\u0D64\u0D65\u0D76-\u0D78\u0D80\u0D81\u0D84\u0D97-\u0D99\u0DB2\u0DBC\u0DBE\u0DBF\u0DC7-\u0DC9\u0DCB-\u0DCE\u0DD5\u0DD7\u0DE0-\u0DF1\u0DF5-\u0E00\u0E3B-\u0E3E\u0E5C-\u0E80\u0E83\u0E85\u0E86\u0E89\u0E8B\u0E8C\u0E8E-\u0E93\u0E98\u0EA0\u0EA4\u0EA6\u0EA8\u0EA9\u0EAC\u0EBA\u0EBE\u0EBF\u0EC5\u0EC7\u0ECE\u0ECF\u0EDA\u0EDB\u0EE0-\u0EFF\u0F48\u0F6D-\u0F70\u0F98\u0FBD\u0FCD\u0FDB-\u0FFF\u10C6\u10C8-\u10CC\u10CE\u10CF\u1249\u124E\u124F\u1257\u1259\u125E\u125F\u1289\u128E\u128F\u12B1\u12B6\u12B7\u12BF\u12C1\u12C6\u12C7\u12D7\u1311\u1316\u1317\u135B\u135C\u137D-\u137F\u139A-\u139F\u13F5-\u13FF\u169D-\u169F\u16F1-\u16FF\u170D\u1715-\u171F\u1737-\u173F\u1754-\u175F\u176D\u1771\u1774-\u177F\u17DE\u17DF\u17EA-\u17EF\u17FA-\u17FF\u180F\u181A-\u181F\u1878-\u187F\u18AB-\u18AF\u18F6-\u18FF\u191D-\u191F\u192C-\u192F\u193C-\u193F\u1941-\u1943\u196E\u196F\u1975-\u197F\u19AC-\u19AF\u19CA-\u19CF\u19DB-\u19DD\u1A1C\u1A1D\u1A5F\u1A7D\u1A7E\u1A8A-\u1A8F\u1A9A-\u1A9F\u1AAE-\u1AFF\u1B4C-\u1B4F\u1B7D-\u1B7F\u1BF4-\u1BFB\u1C38-\u1C3A\u1C4A-\u1C4C\u1C80-\u1CBF\u1CC8-\u1CCF\u1CF7-\u1CFF\u1DE7-\u1DFB\u1F16\u1F17\u1F1E\u1F1F\u1F46\u1F47\u1F4E\u1F4F\u1F58\u1F5A\u1F5C\u1F5E\u1F7E\u1F7F\u1FB5\u1FC5\u1FD4\u1FD5\u1FDC\u1FF0\u1FF1\u1FF5\u1FFF\u200B-\u200F\u202A-\u202E\u2060-\u206F\u2072\u2073\u208F\u209D-\u209F\u20BB-\u20CF\u20F1-\u20FF\u218A-\u218F\u23F4-\u23FF\u2427-\u243F\u244B-\u245F\u2700\u2B4D-\u2B4F\u2B5A-\u2BFF\u2C2F\u2C5F\u2CF4-\u2CF8\u2D26\u2D28-\u2D2C\u2D2E\u2D2F\u2D68-\u2D6E\u2D71-\u2D7E\u2D97-\u2D9F\u2DA7\u2DAF\u2DB7\u2DBF\u2DC7\u2DCF\u2DD7\u2DDF\u2E3C-\u2E7F\u2E9A\u2EF4-\u2EFF\u2FD6-\u2FEF\u2FFC-\u2FFF\u3040\u3097\u3098\u3100-\u3104\u312E-\u3130\u318F\u31BB-\u31BF\u31E4-\u31EF\u321F\u32FF\u4DB6-\u4DBF\u9FCD-\u9FFF\uA48D-\uA48F\uA4C7-\uA4CF\uA62C-\uA63F\uA698-\uA69E\uA6F8-\uA6FF\uA78F\uA794-\uA79F\uA7AB-\uA7F7\uA82C-\uA82F\uA83A-\uA83F\uA878-\uA87F\uA8C5-\uA8CD\uA8DA-\uA8DF\uA8FC-\uA8FF\uA954-\uA95E\uA97D-\uA97F\uA9CE\uA9DA-\uA9DD\uA9E0-\uA9FF\uAA37-\uAA3F\uAA4E\uAA4F\uAA5A\uAA5B\uAA7C-\uAA7F\uAAC3-\uAADA\uAAF7-\uAB00\uAB07\uAB08\uAB0F\uAB10\uAB17-\uAB1F\uAB27\uAB2F-\uABBF\uABEE\uABEF\uABFA-\uABFF\uD7A4-\uD7AF\uD7C7-\uD7CA\uD7FC-\uF8FF\uFA6E\uFA6F\uFADA-\uFAFF\uFB07-\uFB12\uFB18-\uFB1C\uFB37\uFB3D\uFB3F\uFB42\uFB45\uFBC2-\uFBD2\uFD40-\uFD4F\uFD90\uFD91\uFDC8-\uFDEF\uFDFE\uFDFF\uFE1A-\uFE1F\uFE27-\uFE2F\uFE53\uFE67\uFE6C-\uFE6F\uFE75\uFEFD-\uFF00\uFFBF-\uFFC1\uFFC8\uFFC9\uFFD0\uFFD1\uFFD8\uFFD9\uFFDD-\uFFDF\uFFE7\uFFEF-\uFFFB\uFFFE\uFFFF]/g;
  return s.replace(re, "");
}

/**
 * 
 * @param data 
 * @param title 
 * @param absPath path ralative to app initial root path. leading / is optional. 
 */
export function replaceURLHistory(data:any, title:string, absPath: string){
   if(window.location.protocol === 'file:'){ 
     // running inside app
     if(absPath.startsWith('/')){
       absPath = absPath.substr(1);
     }
     const rootPath = window.location.pathname.replace(/index[.]html.*/, 'index.html');
     let path = '';
     if(absPath){
       path = rootPath+'/'+absPath;
     }else{
       path = rootPath;
     }
    //  alert('replaceURLHistory '+path);
     window.history.replaceState(data, title, path);
   }else{
    window.history.replaceState(data, title, absPath);
   }   
}

const PROD_URL = 'https://app.cpnos.cn';
const UAT_URL = 'https://apiserver.unishell.com:8000';

let serverURL = process.env.REACT_APP_WALLET_SERVER_URL || '';

export function getServerURL():string {
  return serverURL;
}
export function setServerURL(url: string){
  info('set server url='+url);
  serverURL = url;
}


export function getProdServerURL(): string {
  return PROD_URL;
}

export function pointingProdServer() {
  return getServerURL() === PROD_URL;
}
export function pointingUATServer() {
  return getServerURL() === UAT_URL
}

export function switchToProdServer() {
  setServerURL(PROD_URL);
}

export function switchToUATServer() {
  setServerURL(UAT_URL);
}

/**
 * wait for <code>delay</code> milliseconds and return value T.
 * 
 * @param value 
 * @param delay 
 */
export async function asyncDelay<T>(value:T, delay:number):Promise<T>{
    return new Promise((resolve, reject)=>{
        setTimeout(()=>{
            resolve(value);
        }, delay);
    })
}

export function getViewportSize() {
    const vw = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
    const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
    
    return {vw, vh}
}

function quantize(n:number, qunit:number):number {
    return Math.floor(n/qunit) * qunit;
}

export function getMaxThumbnailImgSize(){
    // quantize thumbnail image size
    const qunit = 64;
    const {vw, vh} = getViewportSize();

    let maxImgWidth = Math.max(256, Math.min(quantize(vw/2, qunit), 64*8));
    let maxImgHeight = maxImgWidth;

    return {maxImgWidth, maxImgHeight}
}


export function formatDate(d:Date){
    return format(d, 'yyyy-MM-dd');
}

export function formatDateTime(d:Date){
    return format(d, 'yyyy-MM-dd HH:mm:ss');
}


export function formatPrice(price:number, currencySymbol:string, currencyName:string):string{
    return currencySymbol+price.toFixed(2)+' '+currencyName;
}

export function getCurrencyOption(currencyOptionsMap: {[key:string]:CurrencyOption[]},
                                    languageCode: string,
                                    currencyCode: string):CurrencyOption{
    let options = currencyOptionsMap[languageCode];
    if(!options){
        options = currencyOptionsMap[LANG_CODE_ZH_CN];
    }
    let option = options.find(x=> x.code === currencyCode);
    if(!option ){
        throw new Error(`Unkown currency code:${currencyCode}`)
    }

    return option!;
}

export function getPaymentMethodOption(paymentMethodOptionsMap:{[key:string]:PaymentMethodOption[]},
                                       languageCode: string, paymentMethodCode: string):PaymentMethodOption
{
    let options = paymentMethodOptionsMap[languageCode];
    if(!options){
        options = paymentMethodOptionsMap[LANG_CODE_ZH_CN];
    }
    let option = options.find(x=> x.code === paymentMethodCode);
    if(!option ){
        throw new Error(`Unkown payment method code:${paymentMethodCode}`)
    }

    return option!;        
}


export function getStripeErrorByCode (error:StripeError): string {
    // https://stackoverflow.com/questions/23437439/non-english-texts-in-stripe-possible
    // see https://stripe.com/docs/declines/codes for complete list
    const online_payment_error_codes = {
        // account_already_exists: "The email address provided for the creation of a deferred account already has an account associated with it. Use the OAuth flow to connect the existing account to your platform.",
        // account_country_invalid_address: "The country of the business address provided does not match the country of the account. Businesses must be located in the same country as the account.",
        // account_invalid: "The account ID provided as a value for the Stripe-Account header is invalid. Check that your requests are specifying a valid account ID.",
        // account_number_invalid: "The bank account number provided is invalid (e.g., missing digits). Bank account information varies from country to country. We recommend creating validations in your entry forms based on the bank account formats we provide.",
        // alipay_upgrade_required: "This method for creating Alipay payments is not supported anymore. Please upgrade your integration to use Sources instead.",
        amount_too_large: t("支付金额超出上限"),
        amount_too_small: t("支付金额低于下限"),
        api_key_expired: t("接口密钥过期。请联系客服"),
        // balance_insufficient: "The transfer or payout could not be completed because the associated account does not have a sufficient balance available. Create a new transfer or payout using an amount less than or equal to the account’s available balance.",
        // bank_account_exists: "The bank account provided already exists on the specified Customer object. If the bank account should also be attached to a different customer, include the correct customer ID when making the request again.",
        // bank_account_unusable: "The bank account provided cannot be used for payouts. A different bank account must be used.",
        // bank_account_unverified: "Your Connect platform is attempting to share an unverified bank account with a connected account.",
        // bitcoin_upgrade_required: "This method for creating Bitcoin payments is not supported anymore. Please upgrade your integration to use Sources instead.",
        // card_declined: t("The card has been declined. When a card is declined, the error returned also includes the decline_code attribute with the reason why the card was declined. Refer to our decline codes documentation to learn more."),
        // charge_already_captured: "The charge you’re attempting to capture has already been captured. Update the request with an uncaptured charge ID.",
        // charge_already_refunded: "The charge you’re attempting to refund has already been refunded. Update the request to use the ID of a charge that has not been refunded.",
        // charge_disputed: "The charge you’re attempting to refund has been charged back. Check the disputes documentation to learn how to respond to the dispute.",
        // charge_exceeds_source_limit: "This charge would cause you to exceed your rolling-window processing limit for this source type. Please retry the charge later, or contact us to request a higher processing limit.",
        // charge_expired_for_capture: "The charge cannot be captured as the authorization has expired. Auth and capture charges must be captured within seven days.",
        // country_unsupported: "Your platform attempted to create a custom account in a country that is not yet supported. Make sure that users can only sign up in countries supported by custom accounts.",
        // coupon_expired: "The coupon provided for a subscription or order has expired. Either create a new coupon, or use an existing one that is valid.",
        // customer_max_subscriptions: "The maximum number of subscriptions for a customer has been reached. Contact us if you are receiving this error.",
        // email_invalid: "The email address is invalid (e.g., not properly formatted). Check that the email address is properly formatted and only includes allowed characters.",
        expired_card: t("信用卡过期"),
        // idempotency_key_in_use: "The idempotency key provided is currently being used in another request. This occurs if your integration is making duplicate requests simultaneously.",
        // incorrect_address: "The card’s address is incorrect. Check the card’s address or use a different card.",
        incorrect_cvc: t("错误验证码"),
        incorrect_number: t("错误信用卡号"),
        // incorrect_zip: "The card’s postal code is incorrect. Check the card’s postal code or use a different card.",
        // instant_payouts_unsupported: "The debit card provided as an external account does not support instant payouts. Provide another debit card or use a bank account instead.",
        // invalid_card_type: "The card provided as an external account is not a debit card. Provide a debit card or use a bank account instead.",
        // invalid_charge_amount: "The specified amount is invalid. The charge amount must be a positive integer in the smallest currency unit, and not exceed the minimum or maximum amount.",
        invalid_cvc: t("错误验证码"),
        invalid_expiry_month: t("错误信用卡失效年月"),
        invalid_expiry_year: t("错误信用卡失效年月"),
        invalid_number: t("错误信用卡号"),
        // invalid_source_usage: "The source cannot be used because it is not in the correct state (e.g., a charge request is trying to use a source with a pending, failed, or consumed source). Check the status of the source you are attempting to use.",
        // invoice_no_customer_line_items: "An invoice cannot be generated for the specified customer as there are no pending invoice items. Check that the correct customer is being specified or create any necessary invoice items first.",
        // invoice_no_subscription_line_items: "An invoice cannot be generated for the specified subscription as there are no pending invoice items. Check that the correct subscription is being specified or create any necessary invoice items first.",
        // invoice_not_editable: "The specified invoice can no longer be edited. Instead, consider creating additional invoice items that will be applied to the next invoice. You can either manually generate the next invoice or wait for it to be automatically generated at the end of the billing cycle.",
        // invoice_upcoming_none: "There is no upcoming invoice on the specified customer to preview. Only customers with active subscriptions or pending invoice items have invoices that can be previewed.",
        // livemode_mismatch: "Test and live mode API keys, requests, and objects are only available within the mode they are in.",
        // missing: "Both a customer and source ID have been provided, but the source has not been saved to the customer. To create a charge for a customer with a specified source, you must first save the card details.",
        // not_allowed_on_standard_account: "Transfers and payouts on behalf of a Standard connected account are not allowed.",
        // order_creation_failed: "The order could not be created. Check the order details and then try again.",
        // order_required_settings: "The order could not be processed as it is missing required information. Check the information provided and try again.",
        // order_status_invalid: "The order cannot be updated because the status provided is either invalid or does not follow the order lifecycle (e.g., an order cannot transition from created to fulfilled without first transitioning to paid).",
        // order_upstream_timeout: "The request timed out. Try again later.",
        // out_of_inventory: "The SKU is out of stock. If more stock is available, update the SKU’s inventory quantity and try again.",
        // parameter_invalid_empty: "One or more required values were not provided. Make sure requests include all required parameters.",
        // parameter_invalid_integer: "One or more of the parameters requires an integer, but the values provided were a different type. Make sure that only supported values are provided for each attribute. Refer to our API documentation to look up the type of data each attribute supports.",
        // parameter_invalid_string_blank: "One or more values provided only included whitespace. Check the values in your request and update any that contain only whitespace.",
        // parameter_invalid_string_empty: "One or more required string values is empty. Make sure that string values contain at least one character.",
        // parameter_missing: "One or more required values are missing. Check our API documentation to see which values are required to create or modify the specified resource.",
        // parameter_unknown: "The request contains one or more unexpected parameters. Remove these and try again.",
        // parameters_exclusive: "Two or more mutually exclusive parameters were provided. Check our API documentation or the returned error message to see which values are permitted when creating or modifying the specified resource.",
        // payment_intent_authentication_failure: "The provided source has failed authentication. Provide source_data or a new source to attempt to fulfill this PaymentIntent again.",
        // payment_intent_incompatible_payment_method: "The PaymentIntent expected a payment method with different properties than what was provided.",
        // payment_intent_invalid_parameter: "One or more provided parameters was not allowed for the given operation on the PaymentIntent. Check our API reference or the returned error message to see which values were not correct for that PaymentIntent.",
        // payment_intent_payment_attempt_failed: "The latest payment attempt for the PaymentIntent has failed. Check the last_payment_error property on the PaymentIntent for more details, and provide source_data or a new source to attempt to fulfill this PaymentIntent again.",
        // payment_intent_unexpected_state: "The PaymentIntent’s state was incompatible with the operation you were trying to perform.",
        // payment_method_unactivated: "The charge cannot be created as the payment method used has not been activated. Activate the payment method in the Dashboard, then try again.",
        // payment_method_unexpected_state: "The provided payment method’s state was incompatible with the operation you were trying to perform. Confirm that the payment method is in an allowed state for the given operation before attempting to perform it.",
        // payouts_not_allowed: "Payouts have been disabled on the connected account. Check the connected account’s status to see if any additional information needs to be provided, or if payouts have been disabled for another reason.",
        // platform_api_key_expired: "The API key provided by your Connect platform has expired. This occurs if your platform has either generated a new key or the connected account has been disconnected from the platform. Obtain your current API keys from the Dashboard and update your integration, or reach out to the user and reconnect the account.",
        // postal_code_invalid: "The postal code provided was incorrect.",
        // processing_error: "An error occurred while processing the card. Check the card details are correct or use a different card.",
        // product_inactive: "The product this SKU belongs to is no longer available for purchase.",
        rate_limit: t("信用卡支付请求太忙"),
        // resource_already_exists: "A resource with a user-specified ID (e.g., plan or coupon) already exists. Use a different, unique value for id and try again.",
        // resource_missing: "The ID provided is not valid. Either the resource does not exist, or an ID for a different resource has been provided.",
        // routing_number_invalid: "The bank routing number provided is invalid.",
        // secret_key_required: "The API key provided is a publishable key, but a secret key is required. Obtain your current API keys from the Dashboard and update your integration to use them.",
        // sepa_unsupported_account: "Your account does not support SEPA payments.",
        // shipping_calculation_failed: "Shipping calculation failed as the information provided was either incorrect or could not be verified.",
        // sku_inactive: "The SKU is inactive and no longer available for purchase. Use a different SKU, or make the current SKU active again.",
        // state_unsupported: "Occurs when providing the legal_entity information for a U.S. custom account, if the provided state is not supported. (This is mostly associated states and territories.)",
        // tax_id_invalid: "The tax ID number provided is invalid (e.g., missing digits). Tax ID information varies from country to country, but must be at least nine digits.",
        // taxes_calculation_failed: "Tax calculation for the order failed.",
        // testmode_charges_only: "Your account has not been activated and can only make test charges. Activate your account in the Dashboard to begin processing live charges.",
        // tls_version_unsupported: "Your integration is using an older version of TLS that is unsupported. You must be using TLS 1.2 or above.",
        // token_already_used: "The token provided has already been used. You must create a new token before you can retry this request.",
        // token_in_use: "The token provided is currently being used in another request. This occurs if your integration is making duplicate requests simultaneously.",
        // transfers_not_allowed: "The requested transfer cannot be created. Contact us if you are receiving this error.",
        // upstream_order_creation_failed: "The order could not be created. Check the order details and then try again.",
        // url_invalid: t("The URL provided is invalid.")
    };

    let message = t('支付出错');
    // @ts-ignore
    message = online_payment_error_codes[error.code!];
    if(!message){
       message = error.message!;
    }
    return message || t('支付出错');
}

export function getStripeErrorMessage(error:StripeError):string {
    switch(error.type){
        case 'api_connection_error': return t('连接出错')+":"+getStripeErrorByCode(error);
        case 'api_error': return t('支付接口出错')+":"+getStripeErrorByCode(error);
        case 'authentication_error': return t('认证出错')+":"+getStripeErrorByCode(error);
        case 'card_error': return t('信用卡错误')+":"+getStripeErrorByCode(error);
        case 'invalid_request_error': return t('错误请求')+":"+getStripeErrorByCode(error);
        case 'rate_limit_error': return t('支付接口太忙')+":"+getStripeErrorByCode(error);
        case 'validation_error': return t('信用卡验证错误')+":"+getStripeErrorByCode(error);
        case 'idempotency_error': 
        default: return error.type+":"+error.message!;        
    }
}

/**
 * check if alipay and wechat pay supported
 */
export async function getPaymentSupport(): Promise<{supportWechatPay:boolean, supportAlipay:boolean}>{
    let supportAlipay = false;
    let supportWechatPay = false;


    let hasCordova = !!getCordova();
    if (hasCordova) {
        let ali = new Promise<boolean>((resolve, reject)=>{
            checkPaymentAppAvailability(
                (installed:boolean)=> {
                    resolve(installed);
                },
                (installed:boolean)=> {
                }
            );    
        });
        let wechat = new Promise<boolean>((resolve, reject)=>{
            checkPaymentAppAvailability(
                (installed:boolean)=> {
                },
                (installed:boolean)=> {
                    resolve(installed);
                }
            );    
        });
        supportAlipay = await ali;
        supportWechatPay = await wechat;
        return {supportWechatPay, supportAlipay}
    }else{
        return {supportWechatPay, supportAlipay}
    }
}

/**
  * Not accurate on mobile with non default font size settings!
  *  
  * Uses canvas.measureText to compute and return the width of the given text of given font in pixels.
  * 
  * @param {String} text The text to be rendered.
  * @param {String} font The css font descriptor that text is to be rendered with (e.g. "bold 14px verdana").
  * 
  * @see https://stackoverflow.com/questions/118241/calculate-text-width-with-javascript/21015393#21015393
  */
 export function getTextWidthUsingCanvas(text:string, font:string) {
    // create an invisible div under <body>


    // re-use canvas object for better performance
    // @ts-ignore
    const canvas:HTMLCanvasElement = getTextWidthUsingCanvas['canvas'] as HTMLCanvasElement || (getTextWidthUsingCanvas.canvas = document.createElement("canvas"));
    const context = canvas.getContext("2d");
    context!.font = font;
    const metrics = context!.measureText(text);
    return metrics.width;
  }
  
//   function getCssStyle(element, prop) {
//       return window.getComputedStyle(element, null).getPropertyValue(prop);
//   }
  
//   function getCanvasFont(el = document.body) {
//     const fontWeight = getCssStyle(el, 'font-weight') || 'normal';
//     const fontSize = getCssStyle(el, 'font-size') || '16px';
//     const fontFamily = getCssStyle(el, 'font-family') || 'Verdana';
    
//     return `${fontWeight} ${fontSize} ${fontFamily}`;
//   }

export type RatioClass = 'xxxs'|'xxs' | 'xs' | 'sm' | 'md' | 'lg' ;
export const _allRatioClassNames: RatioClass[] = [
    'xxxs','xxs', 'xs', 'sm', 'md', 'lg'
]
export const RatioXXXS = 16, RatioXXS=21, RatioXS=26, RatioSM=70, RatioMD=93, RatioLG=139; 

/**
 * use 16px Verdana M letter to calculate screen width vs M width ratio.
 */
export function calcRationClass(){
    const divM = document.getElementById('1rem-normal-verdana-M');

    let mw = divM ? divM!.getBoundingClientRect().width : 0;
    let mwOriginal = getTextWidthUsingCanvas('M', 'normal 16px Verdana'); // fallback. shouldn't happen
    const {vw} = getViewportSize();
    const ratio = vw/mw;

    let ratioClass:RatioClass = 'xs';
    // the classifications are based on my huawei mate 30 lite
    if(ratio <= RatioXXXS){
        ratioClass = 'xxxs';
    }else if(ratio <= RatioXXS){
        ratioClass = 'xxs';
    }else if(ratio <= RatioXS){
        ratioClass = 'xs';
    }else if(ratio <= RatioSM) {
        ratioClass = 'sm';
    }else if(ratio <= RatioMD){
        ratioClass = 'md';
    }else if(ratio < RatioLG){
        ratioClass = 'lg';
    }else{
        ratioClass = 'lg';
    }

    return {ratioClass, vw,  mw, ratio, mwOriginal}
}

/**
 * Gets only the valid children of a component,
 * and ignores any nullish or falsy child.
 *
 * @param children the children
 */
export function getValidReactChildren(children: React.ReactNode) {
    return React.Children.toArray(children).filter((child) =>
      React.isValidElement(child),
    ) as React.ReactElement[]
  }


/**
 * get image natural dimension from an loaded img element.
 * @param img 
 */
export function realImgDimension(img:HTMLImageElement) {
    let i = new Image();
    i.src = img.src;
    return {
        naturalWidth: i.width, 
        naturalHeight: i.height
    };
}  

export function getElementInnerSize( elm:HTMLElement ){
    let computed = getComputedStyle(elm),
        paddingHeight = (parseInt(computed.paddingTop||'0')) + (parseInt(computed.paddingBottom||'0')),
        paddingWidth = (parseInt(computed.paddingLeft||'0')) + (parseInt(computed.paddingRight||'0'))
        ;
  
    return {innerHeight: elm.clientHeight - paddingHeight, innerWidth: elm.clientWidth - paddingWidth};
}

export function scrollBodyToTop(){
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
}


/**
 * Experiment feature. show a global spinner installed outside React.
 */
export function showGlobalSpinner(){
    const elem = document.getElementById("__unishell_built_in_loader");
    if(elem){
      elem.className = "";
      // in case the app failed to hide it
      setTimeout(()=>{
        hideGlobalSpinner();
      }, 30000);
    }
  }
  
  /**
   * Experiment feature. hide the global spinner installed outside React.
   */
   export function hideGlobalSpinner(){
    const elem = document.getElementById("__unishell_built_in_loader");
    if(elem){
      elem.className = "hide"; 
    }
  }
